import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge } from '../components/ui';
import { useCurrentMember } from '../hooks/useCurrentMember';
import { PageHeader } from '../components/PageHeader';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, isPast, parseISO, format } from 'date-fns';
import { Award, Inbox, Calendar, TrendingUp, CalendarClock, AlertCircle } from 'lucide-react';
import { safeParseISO } from '../lib/dates';
import { deriveAllMilestones } from '../lib/planner';
import { countStale } from '../lib/staleness';
import { loadPlannerOffsets, type PlannerOffsetValues } from '../lib/plannerSettings';
import { CATEGORY_STYLES } from '../components/milestoneColors';
import { Monogram } from '../components/Monogram';
import type { Scholarship } from '@scholarship-app/types';

export function DashboardPage() {
  const { membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const { firstName } = useCurrentMember();
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [plannerOffsets, setPlannerOffsets] = useState<PlannerOffsetValues | null>(null);
  // Ids of milestones the user has marked complete — so the "Coming up" card
  // can exclude them (v0.2.35). Derived milestones don't carry status, so we
  // read it from the persisted timeline_milestones collection.
  const [completedMilestoneIds, setCompletedMilestoneIds] = useState<Set<string>>(new Set());

  // Planner offsets — for the "Coming up" milestone card (v0.2.34).
  useEffect(() => {
    if (!db || !membership) return;
    loadPlannerOffsets(db, membership.householdId).then(setPlannerOffsets);
  }, [membership]);

  // Completed-milestone ids (v0.2.35).
  useEffect(() => {
    if (!db || !membership || !applicantId) return;
    const colRef = collection(
      db, 'households', membership.householdId, 'applicants', applicantId, 'timeline_milestones'
    );
    const unsub = onSnapshot(colRef, (snap) => {
      const done = new Set<string>();
      snap.docs.forEach((d) => {
        const m = d.data() as { status?: string };
        if (m.status === 'complete') done.add(d.id);
      });
      setCompletedMilestoneIds(done);
    }, () => { /* non-fatal: card just won't filter completed */ });
    return () => unsub();
  }, [membership, applicantId]);
  const [inboxCount, setInboxCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Real-time subscription to scholarships so edits made elsewhere reflect
  // here immediately (e.g. when Cole edits an amount on the Scholarships tab).
  useEffect(() => {
    if (!db || !membership || !applicantId) return;
    const colRef = collection(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const list = snap.docs.map((d) => d.data() as Scholarship);
        setScholarships(list);
        setLoading(false);
      },
      (err) => {
        console.error('[Dashboard] scholarships snapshot failed:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [membership, applicantId]);

  // Real-time inbox count.
  useEffect(() => {
    if (!db || !membership) return;
    const q = query(collection(db, 'households', membership.householdId, 'inbox'), where('status', '==', 'new'));
    const unsub = onSnapshot(
      q,
      (snap) => setInboxCount(snap.size),
      (err) => console.error('[Dashboard] inbox snapshot failed:', err)
    );
    return () => unsub();
  }, [membership]);

  if (loading) {
    return <div className="text-soft">Loading dashboard…</div>;
  }

  const active = scholarships.filter((s) => !s.archived && s.status !== 'awarded' && s.status !== 'rejected' && s.status !== 'expired');

  // Stale scholarships worth a review (v0.2.38).
  const staleCount = countStale(scholarships);

  // Awarded scholarships: any with status === 'awarded'. We use status (not
  // result) because status is what the user actively manages on the
  // Scholarship Detail page. The total $ comes from result_amount only — if
  // a scholarship is awarded but result_amount isn't filled in, it counts
  // toward the COUNT but contributes $0 to the total, with a hint to fill it.
  const awardedList = scholarships.filter((s) => s.status === 'awarded');
  const totalAwarded = awardedList.reduce((sum, s) => sum + (s.result_amount ?? 0), 0);
  const awardedCount = awardedList.length;
  const missingAmountCount = awardedList.filter((s) => s.result_amount == null || s.result_amount === 0).length;

  const upcoming = [...active]
    .filter((s) => s.effective_deadline)
    .sort((a, b) => (a.effective_deadline ?? '').localeCompare(b.effective_deadline ?? ''))
    .slice(0, 5);

  // "Coming up" — the next prep milestones from the planner (v0.2.34). Shows
  // milestones from today forward; a small forward-looking window into the
  // Planner tab.
  const todayIso = new Date().toISOString().slice(0, 10);
  const comingUp = plannerOffsets
    ? deriveAllMilestones(active, plannerOffsets)
        .filter((m) => m.targetDate >= todayIso && !completedMilestoneIds.has(m.id))
        .slice(0, 4)
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
        description="Here's where things stand."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-soft text-xs uppercase tracking-wide">
            <Award size={14} /> Active
          </div>
          <div className="mt-1 text-2xl font-semibold">{active.length}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-soft text-xs uppercase tracking-wide">
            <Inbox size={14} /> Inbox
          </div>
          <div className="mt-1 text-2xl font-semibold">{inboxCount}</div>
          <Link to="/inbox" className="text-xs text-forest-700 hover:underline">
            Triage now →
          </Link>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-soft text-xs uppercase tracking-wide">
            <TrendingUp size={14} /> Awarded
          </div>
          <div className="mt-1 text-2xl font-semibold">${totalAwarded.toLocaleString()}</div>
          {awardedCount > 0 && (
            <div className="text-xs text-soft mt-0.5">
              {awardedCount} Scholarship{awardedCount === 1 ? '' : 's'}
            </div>
          )}
          {missingAmountCount > 0 && (
            <div className="text-xs text-amber-700 mt-1">
              {missingAmountCount} missing amount — open & fill in
            </div>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-soft text-xs uppercase tracking-wide">
            <Calendar size={14} /> Total tracked
          </div>
          <div className="mt-1 text-2xl font-semibold">{scholarships.length}</div>
        </Card>
      </div>

      {/* Needs-review summary (v0.2.38) — stale scholarships worth a look. */}
      {staleCount > 0 && (
        <Link
          to="/scholarships"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 hover:bg-amber-100"
        >
          <AlertCircle size={15} className="shrink-0" />
          <span>
            <strong>{staleCount}</strong> scholarship{staleCount === 1 ? '' : 's'} need
            {staleCount === 1 ? 's' : ''} review — passed deadlines, stale entries, or
            unverified links.
          </span>
        </Link>
      )}

      <Card className="p-4">
        <h2 className="font-medium text-ink mb-3">Upcoming deadlines</h2>
        {upcoming.length === 0 ? (
          <p className="text-sm text-soft">No upcoming deadlines.</p>
        ) : (
          <ul className="divide-y divide-line">
            {upcoming.map((s) => {
              // Defensive: effective_deadline may be an unparseable string on
              // scholarships promoted before v0.2.20.1's promotion fix.
              const parsedDeadline = safeParseISO(s.effective_deadline);
              const deadlinePast = parsedDeadline ? isPast(parsedDeadline) : false;
              return (
                <li key={s.scholarship_id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2.5">
                    <Monogram name={s.sponsor || s.name} size={32} />
                    <div className="min-w-0">
                    <Link to={`/scholarships/${s.scholarship_id}`} className="text-sm font-medium text-ink hover:text-forest-700 truncate block">
                      {s.name}
                    </Link>
                    <p className="text-xs text-soft truncate">{s.sponsor}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant={deadlinePast ? 'red' : 'amber'}>
                      {parsedDeadline
                        ? (deadlinePast ? 'Past due' : formatDistanceToNow(parsedDeadline, { addSuffix: true }))
                        : 'Date TBD'}
                    </Badge>
                    {s.linked_admission_deadline && (
                      <div className="text-xs text-amber-700 mt-0.5">admission-linked</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Coming up — next planner milestones (v0.2.34) */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium text-ink flex items-center gap-1.5">
            <CalendarClock size={16} /> Coming up
          </h2>
          <Link to="/planner" className="text-xs text-forest-700 hover:underline">
            Open planner →
          </Link>
        </div>
        {comingUp.length === 0 ? (
          <p className="text-sm text-soft">
            No prep milestones coming up. The Planner builds these from your
            scholarship deadlines.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {comingUp.map((m) => (
              <li key={m.id} className="py-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Monogram name={m.scholarshipName} size={28} />
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${CATEGORY_STYLES[m.category].dot}`} />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink">{m.title}</span>
                    <p className="text-xs text-soft truncate">{m.scholarshipName}</p>
                  </div>
                </div>
                <span className="text-xs text-soft shrink-0">
                  {format(parseISO(m.targetDate), 'MMM d')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
